import React from 'react';
import { escape, set } from 'lodash';
import {
  Dac7FormDataTypes,
  PrivateFormData,
  SoleTraderFormData,
  BusinessFormData,
} from '@olxeu-a2b/ui/typings/dac7';
import useMutateDac7Data from './useMutateDac7Data';
import useDac7Validate from './useDac7Validate';

const FORM_INPUT_NAME_PREFIX = 'dac7_';

const takeOnlyDac7RelatedInputs = (element: Element): boolean =>
  element.getAttribute('name')?.startsWith(FORM_INPUT_NAME_PREFIX);

const isDac7Private = (dac7Data: Dac7FormDataTypes): dac7Data is PrivateFormData =>
  dac7Data.type === 'PRIVATE';
const isDac7Business = (dac7Data: Dac7FormDataTypes): dac7Data is BusinessFormData =>
  dac7Data.type === 'BUSINESS';
const isDac7SoleTrader = (dac7Data: Dac7FormDataTypes): dac7Data is SoleTraderFormData =>
  dac7Data.type === 'SOLE_TRADER';

const sanitizeValue = (value: string | undefined): string | undefined =>
  value ? escape(value.trim()) : value;

const sanitizeVatValue = (value: string | undefined): string | undefined =>
  value ? escape(value.toLocaleUpperCase().trim()) : value;

const sanitize = (formData: Readonly<Dac7FormDataTypes>): Dac7FormDataTypes => {
  if (isDac7Private(formData)) {
    return {
      ...formData,
      taxDetails: {
        taxNumbersDetails: Array.isArray(formData.taxDetails)
          ? formData.taxDetails.taxNumbersDetails.map(({ taxId, ...rest }) => ({
            ...rest,
            taxId: sanitizeValue(taxId),
          }))
          : formData.taxDetails.taxNumbersDetails,
      },
      financialDetails: formData.financialDetails
        ? {
          ...formData.financialDetails,
          iban: sanitizeValue(formData.financialDetails.iban)?.replace(/\s/g, ''),
        }
        : formData.financialDetails,
    };
  }

  if (isDac7SoleTrader(formData)) {
    return {
      ...formData,
      taxDetails: {
        vatNumber: sanitizeVatValue(formData.taxDetails.vatNumber),
        taxNumbersDetails: Array.isArray(formData.taxDetails.taxNumbersDetails)
          ? formData.taxDetails.taxNumbersDetails.map(({ taxId, ...rest }) => ({
            ...rest,
            taxId: sanitizeValue(taxId),
          }))
          : formData.taxDetails.taxNumbersDetails,
      },
      financialDetails: formData.financialDetails
        ? {
          ...formData.financialDetails,
          iban: sanitizeValue(formData.financialDetails.iban)?.replace(/\s/g, ''),
        }
        : formData.financialDetails,
    };
  }

  if (isDac7Business(formData)) {
    return {
      ...formData,
      businessDetails: {
        companyName: sanitizeValue(formData.businessDetails.companyName),
      },
      taxDetails: {
        vatNumber: sanitizeVatValue(formData.taxDetails.vatNumber),
        businessRegistrationNumber: sanitizeValue(formData.taxDetails.businessRegistrationNumber),
        taxNumbersDetails: Array.isArray(formData.taxDetails.taxNumbersDetails)
          ? formData.taxDetails.taxNumbersDetails.map(({ taxId, ...rest }) => ({
            ...rest,
            taxId: sanitizeValue(taxId),
          }))
          : formData.taxDetails.taxNumbersDetails,
      },
      financialDetails: formData.financialDetails
        ? {
          ...formData.financialDetails,
          iban: sanitizeValue(formData.financialDetails.iban)?.replace(/\s/g, ''),
        }
        : formData.financialDetails,
    };
  }

  return formData;
};

// Takes the current form input element, grabs its name and value, strips the "dac7_" prefix from the input name
// the part of the input name left is the path for lodash._set to apply the input value to the payload object (Dac7Data).
// Example: dac7_addresses.0.houseNumber -> addresses.0.houseNumber -> lodash._set -> { addresses[{houseNumber: value}]}
const applyValuesFromInputToPayload = (
  accum: Dac7FormDataTypes,
  currentElement: HTMLInputElement,
): Dac7FormDataTypes => {
  const curPathInit = currentElement.name.split(FORM_INPUT_NAME_PREFIX)[1];
  set(accum, curPathInit, currentElement.value);
  return accum;
};

const collectDac7FormData = (
  formControls: Readonly<HTMLFormControlsCollection>,
): Dac7FormDataTypes =>
  Array.from(formControls)
    .filter(takeOnlyDac7RelatedInputs)
    .reduce(applyValuesFromInputToPayload, {} as Dac7FormDataTypes);

const useSubmit = () => {
  const { validate, validationResult } = useDac7Validate();
  const { isLoading, mutate, error, successResult } = useMutateDac7Data();

  const formSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const dac7Form = e.target as HTMLFormElement;
    const dac7FormData = sanitize(collectDac7FormData(dac7Form.elements));

    if (!validate(dac7FormData)) {
      return;
    }

    mutate(dac7FormData);
  };

  return {
    formSubmit,
    isSubmitting: isLoading,
    submitError: error,
    submitSuccess: successResult,
    validationResult,
  };
};

export default useSubmit;
